import React, { useEffect, useState } from "react";
import { obterConector, testarConector, removerCredenciaisConector } from "../services/api";

/**
 * Conector do ATS: a empresa liga o eaiRH ao próprio sistema de recrutamento.
 *
 * Por que a empresa preenche em vez de nós: o token é credencial. Pedir por e-mail ou WhatsApp é exatamente
 * como credencial vaza. Aqui quem opera o ATS digita direto, o valor vai cifrado para o cofre e nunca mais é
 * exibido — nem para ela, nem para nós.
 *
 * Só três campos. Tudo o mais (id do formulário, fluxos, mapa dos campos) é descoberto a partir do link:
 * número digitado à mão é número digitado errado, e errado aqui significa candidatura no processo de outra vaga.
 */
export default function ConectorAts() {
  const [url, setUrl] = useState("");
  const [usuario, setUsuario] = useState("");
  const [token, setToken] = useState("");
  const [temToken, setTemToken] = useState(false);
  const [trocando, setTrocando] = useState(false);
  const [testando, setTestando] = useState(false);
  const [res, setRes] = useState(null);
  const [erro, setErro] = useState(null);
  const [removendo, setRemovendo] = useState(false);

  useEffect(() => {
    obterConector()
      .then((c) => { setUrl(c.url || ""); setUsuario(c.usuario || ""); setTemToken(!!c.temToken); })
      .catch(() => {});
  }, []);

  const testar = async () => {
    setTestando(true); setErro(null); setRes(null);
    try {
      const r = await testarConector({ url, usuario, token: token || null });
      setRes(r);
      if (token) { setToken(""); setTemToken(true); setTrocando(false); }
    } catch (e) { setErro(e.message); }
    finally { setTestando(false); }
  };

  // Credenciada = tem usuário E token. Meio cadastro não lê nada, então não pode se anunciar como pronto.
  const credenciada = temToken && !!usuario;

  const remover = async () => {
    setRemovendo(true); setErro(null); setRes(null);
    try { await removerCredenciaisConector(); setUsuario(""); setToken(""); setTemToken(false); setTrocando(false); }
    catch (e) { setErro(e.message); }
    finally { setRemovendo(false); }
  };

  const inp = "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary";

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
      <h2 className="font-bold text-gray-900">Conector do sistema de recrutamento</h2>
      <p className="text-sm text-gray-600 mt-1 mb-4">
        Faz a candidatura recebida aqui entrar direto no seu sistema — e, com as credenciais, permite avisar o
        candidato conforme ele avança no processo.
      </p>

      {/* Passo a passo na TELA, não só no e-mail que pediu a configuração. Quem vai preencher isto é a pessoa
          que opera o ATS — normalmente não é quem recebeu o e-mail, e o recado se perde no repasse. Aberto por
          padrão enquanto não há credencial: é exatamente aí que ele é necessário. */}
      <details open={!credenciada} className="mb-4 rounded-xl border border-gray-200 bg-gray-50 p-3">
        <summary className="cursor-pointer text-sm font-semibold text-gray-700">Como conectar (3 minutos)</summary>
        <ol className="mt-2 space-y-1.5 text-sm text-gray-600 list-decimal pl-5">
          <li>
            No seu sistema de recrutamento, escolha (ou crie) um usuário e gere a <strong>chave de API</strong> dele.
          </li>
          <li>
            Confira que esse usuário tem <strong>permissão de leitura no fluxo das candidaturas</strong>.
            É o passo que mais dá problema: sem a permissão, a chave conecta e não devolve nada.
          </li>
          <li>Confira o <strong>link do formulário</strong> abaixo — se já veio preenchido, é o que estamos usando hoje.</li>
          <li>Preencha <strong>Usuário</strong> e <strong>Token</strong> e clique em <strong>Testar conexão</strong>.</li>
          <li>
            Os dois resultados precisam ficar verdes. Se algum falhar, a mensagem que aparece é a do
            <strong> seu próprio sistema</strong> — não uma tradução nossa.
          </li>
        </ol>
        <p className="mt-2 text-[11px] text-gray-400">
          O token é gravado cifrado e não é exibido depois de salvo — nem para você, nem para nós. Você pode
          removê-lo a qualquer momento.
        </p>
      </details>

      <label className="block text-sm font-semibold text-gray-700 mb-1">Link do formulário de candidatura</label>
      <input className={inp} value={url} onChange={(e) => setUrl(e.target.value)}
        placeholder="cole aqui o mesmo link do botão “candidatar-se” do seu site" />
      <p className="text-[11px] text-gray-400 mt-1 mb-4">
        Do link saem o formulário, os fluxos e os campos. Você não precisa preencher nenhum código.
      </p>

      {/* Estado, não rótulo fixo: "opcional" numa tela vazia não diz o que fazer, e "guardado" numa tela cheia
          não diz de quem. O título muda conforme já existe credencial ou não. */}
      <p className="text-sm font-semibold text-gray-700 mb-2">
        {credenciada
          ? <>Credenciais de leitura <span className="font-normal text-green-700">— cadastradas</span></>
          : <>Cadastre o usuário e o token <span className="font-normal text-gray-400">(opcional)</span></>}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Usuário</label>
          <input className={inp} value={usuario} onChange={(e) => setUsuario(e.target.value)}
            placeholder="usuário do seu sistema" />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Token</label>
          {temToken && !trocando ? (
            <div className="flex items-center gap-2 rounded-lg border border-dashed border-gray-300 bg-gray-50 px-3 py-2">
              <span className="rounded bg-green-100 text-green-800 text-[11px] font-bold px-1.5 py-0.5">guardado</span>
              <span className="text-xs text-gray-500 flex-1">não é exibido depois de salvo</span>
              <button type="button" onClick={() => setTrocando(true)}
                className="text-xs font-semibold text-primary hover:underline">trocar</button>
            </div>
          ) : (
            <input className={inp} type="password" value={token} onChange={(e) => setToken(e.target.value)}
              placeholder="token do seu sistema" autoComplete="off" />
          )}
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-baseline gap-x-3">
        <p className="text-[11px] text-gray-400 flex-1 min-w-[240px]">
          Sem as credenciais a candidatura continua entrando normalmente. Elas servem só para ler o andamento e
          avisar o candidato. O usuário precisa ter <strong>permissão de leitura no fluxo das candidaturas</strong> —
          sem ela o token conecta e não devolve nada.
        </p>
        {credenciada && (
          <button type="button" onClick={remover} disabled={removendo}
            className="text-[11px] font-semibold text-red-600 hover:underline disabled:opacity-50">
            {removendo ? "removendo..." : "remover credenciais"}
          </button>
        )}
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button onClick={testar} disabled={testando || !url}
          className="rounded-xl bg-primary hover:bg-brand disabled:opacity-50 text-white font-semibold px-5 py-2 text-sm">
          {testando ? "Testando..." : "Testar conexão"}
        </button>
        {/* O resumo tem que concordar com o que está logo abaixo: "tudo certo" com um ✗ na tela faz a pessoa
            parar de confiar no teste, e um teste em que não se confia não serve para nada. */}
        {res && <span className={`text-sm font-semibold ${res.ok ? "text-green-700" : "text-amber-700"}`}>
          {res.ok ? "Conectado" : "Não conectou"}
        </span>}
      </div>

      {erro && <p role="alert" className="mt-3 text-sm text-red-600">{erro}</p>}

      {res && (
        <div className="mt-4 space-y-2">
          {res.passos.map((p, i) => (
            <div key={i} className={`rounded-xl border p-3 ${p.ok ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`}>
              <div className="flex items-start gap-2">
                <span className={`font-bold ${p.ok ? "text-green-700" : "text-red-700"}`}>{p.ok ? "✓" : "✕"}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-gray-900">{p.nome}</p>
                  {p.detalhe && <p className="text-xs text-gray-600 mt-0.5">{p.detalhe}</p>}
                  {/* Resposta CRUA do outro sistema, sem tradução: quem lê isto opera o ATS e entende o
                      próprio erro melhor do que qualquer texto que a gente escrevesse por cima.
                      SÓ QUANDO DEU ERRADO. Num passo verde a caixa preta não informa, assusta: o parceiro lê
                      "conectado" e vê um bloco de HTTP 500 embaixo, e volta a achar que fez algo errado. O que
                      falha do NOSSO lado a gente lê no log do servidor, não na tela dele. */}
                  {!p.ok && p.respostaCrua && (
                    <pre className="mt-2 max-h-40 overflow-auto rounded-lg bg-gray-900 text-gray-100 text-[11px] p-2 whitespace-pre-wrap break-words">
                      {p.respostaCrua}
                    </pre>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* A lista de campos reconhecidos saiu daqui de propósito. Quem cadastra a credencial quer saber uma
              coisa só: conectou ou não. Tudo que a gente descobre do formulário é bastidor nosso — mostrar isso
              transforma uma resposta de uma linha numa tela cheia de informação que ninguém do outro lado precisa
              conferir, e foi assim que o parceiro passou a caçar problema que não era dele. */}
        </div>
      )}
    </div>
  );
}
